import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtEur, STATE_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { BarChart3, TrendingUp, Users, FileText, MapPin, Package, Clock, Percent, Filter, X, Briefcase, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const CSC_BLUE = "#0F2C59";
const CSC_YELLOW = "#FACC15";

const formatHours = (h) => {
  if (!h || h <= 0) return "—";
  if (h < 24) return `${h.toFixed(1)}h`;
  const days = h / 24;
  return `${days.toFixed(1)}g`;
};

const KpiCard = ({ icon: Icon, label, value, tint = "#E6F0FF", color = CSC_BLUE, testId }) => (
  <Card className="p-4 md:p-5" data-testid={testId}>
    <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-mono">{label}</div>
    <div className="mt-2 flex items-center gap-3">
      <Icon className="w-8 h-8 p-1.5 rounded-md shrink-0" style={{ background: tint, color }} />
      <div className="text-2xl md:text-3xl font-display font-bold leading-none" style={{ color }}>{value}</div>
    </div>
  </Card>
);

const BarRow = ({ label, value, max, color = CSC_BLUE, valueFmt }) => {
  const pct = max > 0 ? Math.max((value / max) * 100, 4) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 md:w-32 text-xs md:text-sm text-slate-600 truncate">{label}</div>
      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
        <div
          className="h-full flex items-center justify-end px-2 text-white text-xs font-mono font-semibold"
          style={{ width: `${pct}%`, background: color }}
        >
          {valueFmt ? valueFmt(value) : value}
        </div>
      </div>
    </div>
  );
};

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async (df, dt) => {
    setLoading(true);
    const params = {};
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    try {
      const { data } = await api.get("/reports/summary", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const trend = useMemo(() => {
    if (!data?.monthly_trend) return [];
    return data.monthly_trend.map((m) => ({
      mese: m.mese,
      Offerte: m.count,
      Valore: m.valore,
    }));
  }, [data]);

  const perStatoData = useMemo(() => {
    if (!data?.per_stato) return [];
    return Object.entries(data.per_stato).map(([k, v]) => ({
      stato: STATE_LABELS[k] || k,
      count: v,
    }));
  }, [data]);

  if (loading && !data) {
    return <div className="text-center py-16 text-slate-400" data-testid="reports-loading">Caricamento…</div>;
  }
  if (!data) return null;

  const perUtenteMax = Math.max(1, ...Object.values(data.per_utente || {}));
  const perProvinciaMax = Math.max(1, ...Object.values(data.per_provincia || {}));
  const topServiziMax = Math.max(1, ...(data.top_servizi || []).map((s) => s.count));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Analytics</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0F2C59]">Report avanzati</h1>
          <p className="text-slate-500 text-sm md:text-base">Conversione, tempi, servizi, province e trend nel tempo.</p>
        </div>
        <Card className="p-3 md:p-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-widest">
            <Filter className="w-3.5 h-3.5" /> Periodo
          </div>
          <div>
            <Label className="text-xs">Da</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 h-9 w-[150px]"
              data-testid="report-date-from"
            />
          </div>
          <div>
            <Label className="text-xs">A</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 h-9 w-[150px]"
              data-testid="report-date-to"
            />
          </div>
          <Button
            onClick={() => load(from, to)}
            className="h-9 bg-[#0F2C59] hover:bg-[#0F2C59]/90"
            data-testid="report-apply-filter"
          >
            Applica
          </Button>
          <div className="border-l border-slate-200 h-9 mx-1" />
          <Button
            variant="outline"
            onClick={() => {
              const params = new URLSearchParams({ format: "xlsx" });
              if (from) params.set("date_from", from);
              if (to) params.set("date_to", to);
              const url = `${API_BASE}/reports/export?${params.toString()}`;
              const w = window.open(url, "_blank");
              if (!w) window.location.href = url;
            }}
            className="h-9 border-green-600 text-green-700 hover:bg-green-50"
            data-testid="report-export-xlsx"
            title="Scarica report completo in Excel (multi-sheet)"
          >
            <Download className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const params = new URLSearchParams({ format: "csv" });
              if (from) params.set("date_from", from);
              if (to) params.set("date_to", to);
              const url = `${API_BASE}/reports/export?${params.toString()}`;
              const w = window.open(url, "_blank");
              if (!w) window.location.href = url;
            }}
            className="h-9"
            data-testid="report-export-csv"
            title="Scarica report in CSV"
          >
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          {(from || to) && (
            <Button
              variant="ghost"
              onClick={() => {
                setFrom("");
                setTo("");
                load();
              }}
              className="h-9"
              data-testid="report-clear-filter"
            >
              <X className="w-4 h-4 mr-1" /> Reset
            </Button>
          )}
        </Card>
      </div>

      {/* KPI riga 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard icon={FileText} label="Offerte" value={data.totale_create} testId="kpi-total-created" />
        <KpiCard icon={TrendingUp} label="Valore totale" value={fmtEur(data.valore_totale)} tint="#DCFCE7" color="#166534" testId="kpi-total-value" />
        <KpiCard icon={Percent} label="Tasso conversione" value={`${data.tasso_conversione}%`} tint="#FEF9C3" color="#854D0E" testId="kpi-conversion-rate" />
        <KpiCard icon={Users} label="Utenti attivi" value={Object.keys(data.per_utente || {}).length} testId="kpi-active-users" />
      </div>

      {/* KPI riga 2 - valori e tempi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard icon={TrendingUp} label="Valore inviato" value={fmtEur(data.valore_inviato)} tint="#E6F0FF" color={CSC_BLUE} testId="kpi-value-sent" />
        <KpiCard icon={TrendingUp} label="Valore accettato" value={fmtEur(data.valore_accettato)} tint="#DCFCE7" color="#166534" testId="kpi-value-accepted" />
        <KpiCard icon={Clock} label="Tempo medio approvazione" value={formatHours(data.avg_approval_hours)} tint="#FEF9C3" color="#854D0E" testId="kpi-avg-approval" />
        <KpiCard icon={Clock} label="Tempo medio accettazione" value={formatHours(data.avg_acceptance_hours)} tint="#FCE7F3" color="#9F1239" testId="kpi-avg-acceptance" />
      </div>

      {/* Trend mensile */}
      <Card className="p-4 md:p-6" data-testid="chart-monthly-trend">
        <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Andamento mensile
        </div>
        {trend.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">Nessun dato per il periodo selezionato.</div>
        ) : (
          <div className="w-full h-[280px]">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="mese" stroke="#64748B" style={{ fontSize: 12 }} />
                <YAxis yAxisId="left" stroke={CSC_BLUE} style={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#166534"
                  style={{ fontSize: 12 }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                />
                <Tooltip
                  formatter={(value, name) => (name === "Valore" ? fmtEur(value) : value)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="Offerte" stroke={CSC_BLUE} strokeWidth={2.5} dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="Valore" stroke="#166534" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Per stato - bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Card className="p-4 md:p-6" data-testid="chart-per-stato">
          <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Distribuzione per stato
          </div>
          {perStatoData.length === 0 ? (
            <div className="text-slate-400 text-sm py-8 text-center">Nessun dato.</div>
          ) : (
            <div className="w-full h-[260px]">
              <ResponsiveContainer>
                <BarChart data={perStatoData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="stato" stroke="#64748B" style={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} interval={0} />
                  <YAxis stroke="#64748B" style={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="count" fill={CSC_YELLOW} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-4 md:p-6" data-testid="chart-per-utente">
          <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> Offerte per utente
          </div>
          <div className="space-y-2">
            {Object.entries(data.per_utente || {})
              .sort(([, a], [, b]) => b - a)
              .map(([user, count]) => (
                <BarRow key={user} label={user} value={count} max={perUtenteMax} color={CSC_BLUE} />
              ))}
            {Object.keys(data.per_utente || {}).length === 0 && (
              <div className="text-slate-400 text-sm">Nessun dato</div>
            )}
          </div>
        </Card>
      </div>

      {/* Provincia + Top servizi */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Card className="p-4 md:p-6" data-testid="chart-per-provincia">
          <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Offerte per provincia
          </div>
          <div className="space-y-2">
            {Object.entries(data.per_provincia || {})
              .sort(([, a], [, b]) => b - a)
              .map(([prov, count]) => (
                <BarRow key={prov} label={prov} value={count} max={perProvinciaMax} color="#0891B2" />
              ))}
            {Object.keys(data.per_provincia || {}).length === 0 && (
              <div className="text-slate-400 text-sm">Nessun dato</div>
            )}
          </div>
        </Card>

        <Card className="p-4 md:p-6" data-testid="chart-top-servizi">
          <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2">
            <Package className="w-4 h-4" /> Top servizi
          </div>
          <div className="space-y-2">
            {(data.top_servizi || []).map((s) => (
              <div key={s.nome} className="flex items-center gap-3">
                <div className="w-32 md:w-40 text-xs md:text-sm text-slate-600 truncate" title={s.nome}>
                  {s.nome}
                </div>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full flex items-center justify-between px-2 text-[#0F2C59] text-xs font-mono font-semibold"
                    style={{ width: `${Math.max((s.count / topServiziMax) * 100, 4)}%`, background: CSC_YELLOW }}
                  >
                    <span>{s.count}</span>
                    <span className="text-[10px] opacity-80">{fmtEur(s.valore)}</span>
                  </div>
                </div>
              </div>
            ))}
            {(data.top_servizi || []).length === 0 && (
              <div className="text-slate-400 text-sm">Nessun dato</div>
            )}
          </div>
        </Card>
      </div>

      {/* ATECO sections */}
      <Card className="p-4 md:p-6" data-testid="chart-per-ateco-sezione">
        <div className="font-display font-semibold text-[#0F2C59] mb-1 flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Offerte per settore ATECO
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Raggruppamento per sezione ATECO 2025 (A–U) determinata dal codice del cliente.
        </p>
        {(!data.per_ateco_sezione || data.per_ateco_sezione.length === 0) ? (
          <div className="text-slate-400 text-sm py-8 text-center">Nessun dato.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="w-full h-[280px]">
              <ResponsiveContainer>
                <BarChart data={data.per_ateco_sezione} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="sezione" stroke="#64748B" style={{ fontSize: 12 }} />
                  <YAxis stroke="#64748B" style={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }}
                    formatter={(value, name, props) => [
                      name === "valore" ? fmtEur(value) : value,
                      name === "valore" ? "Fatturato" : "Offerte",
                    ]}
                    labelFormatter={(l) => {
                      const it = data.per_ateco_sezione.find((x) => x.sezione === l);
                      return it ? `${l} — ${it.nome}` : l;
                    }}
                  />
                  <Bar dataKey="count" fill={CSC_BLUE} radius={[6, 6, 0, 0]} name="Offerte" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 overflow-y-auto max-h-[280px] pr-2">
              {data.per_ateco_sezione.map((s) => (
                <div key={s.sezione} className="flex items-start gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-8 h-8 rounded-md bg-[#0F2C59] text-white font-display font-bold text-sm flex items-center justify-center shrink-0">
                    {s.sezione}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-700 truncate" title={s.nome}>{s.nome}</div>
                    <div className="text-xs text-slate-500 font-mono flex gap-3 mt-0.5">
                      <span>{s.count} offert{s.count === 1 ? "a" : "e"}</span>
                      <span className="text-green-700 font-semibold">{fmtEur(s.valore)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
